import type { JSX } from 'preact';
import './MdLite.css';

// components/MdLite.tsx — R3b: the radar's own model-authored markdown
// (proposal body_md, item reading_md) rendered plainly.
//
// packages/web carries no markdown library (`marked`/`markdown` grepped
// across the whole package before writing this — nothing). The house
// already has a precedent for exactly this content — screens/Review.tsx's
// own SummaryBlock, which renders a model's markdown summary by splitting
// on lines and stripping `#`/`**` rather than parsing: "headings keep their
// weight, and nothing is silently dropped." This factors that same idiom
// out so RadarProposal and RadarTopic share it instead of a second copy —
// never a new renderer, per the R3b spec's own instruction.
//
// R3e (C, tunnel walk feedback): inline code spans (`` ` ``) were rendering
// literally — every marker this renderer knows about is stripped, backticks
// included, never just `**`. `dedupeTitle` drops the reading's FIRST heading
// line when it repeats a title already shown above it (the item review's own
// "### n. title" block, fleet/radar/lib.ts's parseItemBlocks) — only the
// first heading is ever compared; a later heading (e.g. "Sources") renders
// untouched regardless of match.
//
// C1 (2026-09-28) — `[[slug]]` wikilinks, over a wiki page's body (a Record
// page, not a radar reading — nothing radar renders carries `[[...]]`
// today, so this is purely additive). `pythia_note_get`'s own result carries
// `outlinks` (resolved: {slug, title}) and `unresolved_links` (a slug named
// nowhere), which is how the caller — never this component — already knows
// which is which; MdLite only needs the two lists to decide how a given
// `[[slug]]` token renders. A resolved link is a real link (`onOpenPage`,
// route-agnostic — this component holds no router); an unresolved one
// renders as plain text with a named "not written" mark (voice canon §7:
// meaning lives in words, never colour alone).
function stripMarks(text: string): string {
  return text.replace(/\*\*/g, '').replace(/`/g, '');
}

export interface MdLiteWikilinks {
  outlinks: Array<{ slug: string; title: string }>;
  unresolvedLinks: string[];
  onOpenPage: (slug: string) => void;
}

const WIKILINK_RE = /\[\[([^[\]]+)\]\]/g;

/** Splits one line's text on `[[slug]]` tokens, returning a mixed array of
 *  plain strings and rendered link/unresolved nodes preact can place
 *  directly as children. A token naming neither an outlink nor an
 *  unresolved link (should not happen — pythia_note_get names every
 *  `[[...]]` it parsed as one or the other) renders as plain unresolved
 *  text rather than vanishing. */
function renderWikilinks(text: string, wikilinks: MdLiteWikilinks, keyPrefix: string) {
  if (!text.includes('[[')) return text;
  const parts: Array<string | JSX.Element> = [];
  let lastIndex = 0;
  let i = 0;
  let match: RegExpExecArray | null;
  WIKILINK_RE.lastIndex = 0;
  while ((match = WIKILINK_RE.exec(text))) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const slug = match[1]!.trim();
    const resolved = wikilinks.outlinks.find((o) => o.slug === slug);
    if (resolved) {
      parts.push(
        <a
          key={`${keyPrefix}-wl-${i++}`}
          class="mdlite__wikilink"
          href={`/record/page/${resolved.slug}`}
          onClick={(e) => {
            e.preventDefault();
            wikilinks.onOpenPage(resolved.slug);
          }}
        >
          {resolved.title}
        </a>
      );
    } else {
      parts.push(
        <span key={`${keyPrefix}-wl-${i++}`} class="mdlite__wikilink mdlite__wikilink--unresolved">
          {slug} <span class="mdlite__unwritten">not written</span>
        </span>
      );
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

function normalizeForCompare(line: string): string {
  return stripMarks(line)
    .replace(/^#{1,3}\s*/, '')
    .replace(/^\d+\.\s*/, '')
    .trim()
    .toLowerCase();
}

export function MdLite({
  md,
  dedupeTitle,
  wikilinks,
}: {
  md: string;
  dedupeTitle?: string | null;
  /** C1 — present only on a Record page's own body; every other caller
   *  (radar's proposal/reading markdown, which carries no `[[...]]` today)
   *  omits it and gets byte-identical rendering to before this chunk. */
  wikilinks?: MdLiteWikilinks;
}) {
  const normalizedTitle = dedupeTitle ? normalizeForCompare(dedupeTitle) : null;
  let firstHeadingSeen = false;
  return (
    <div class="mdlite">
      {md.split('\n').map((line, i) => {
        const heading = /^#{1,3}\s+/.test(line);
        if (heading && !firstHeadingSeen) {
          firstHeadingSeen = true;
          if (normalizedTitle && normalizeForCompare(line) === normalizedTitle) return null;
        }
        const text = stripMarks(line.replace(/^#{1,3}\s+/, ''));
        if (!text.trim()) return null;
        const key = `${i}-${text.slice(0, 12)}`;
        return (
          <div class={heading ? 'mdlite__h' : 'mdlite__p'} key={key}>
            {wikilinks ? renderWikilinks(text, wikilinks, key) : text}
          </div>
        );
      })}
    </div>
  );
}
